import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { loadRuntimeConfig } from "./app/config";
import { ConfigProvider } from "./app/config-context";
import { ConfigErrorPage } from "./app/config-error-page";
import "./app/features";
import { queryClient } from "./app/query";
import { createAppRouter } from "./app/router";
import { AuthProvider } from "./features/auth/auth-context";
import { createAuth } from "./features/auth/create-auth";
import { initI18n } from "./i18n";
import "@fontsource/atkinson-hyperlegible-next/400.css";
import "@fontsource/atkinson-hyperlegible-next/500.css";
import "@fontsource/atkinson-hyperlegible-next/600.css";
import "@fontsource/atkinson-hyperlegible-next/700.css";
import "@fontsource/atkinson-hyperlegible-mono/400.css";
import "@fontsource/atkinson-hyperlegible-mono/500.css";
import "./styles.css";

const element = document.getElementById("root");
if (!element) throw new Error("index.html has no #root element");
const root = createRoot(element);
initI18n();

// The configuration is loaded before the app; without it only an error page renders.
loadRuntimeConfig().then(
  (config) => {
    const auth = createAuth(config);
    const router = createAppRouter(auth);
    root.render(
      <StrictMode>
        <ConfigProvider config={config}>
          <AuthProvider auth={auth}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </AuthProvider>
        </ConfigProvider>
      </StrictMode>,
    );
  },
  (error: unknown) =>
    root.render(
      <ConfigErrorPage message={error instanceof Error ? error.message : String(error)} />,
    ),
);
